import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Advice, Button, Modal, Text } from '@Sage/hoi-poi-ui';
import { isEqual } from '@Sage/ssm-utils';
import { CopilotContext } from '@Sage/ssm-web5';
import { useDispatch, useSelector } from 'react-redux';

import { EntityCrudActions, EntityModalActions } from 'actions';
import {
    ACTIVITIES,
    AGENDA,
    COMPANIES,
    CONTACTS,
    KPIS,
    OPPORTUNITIES,
    SALESORDERS,
    TASKS,
} from 'constants/Entities';
import UsageLimitInfo from 'containers/components/UsageLimitInfo';
import SalesOrdersEmbeddedCrud from 'containers/SalesOrders/SalesOrdersEmbeddedCrud';
import useSalesOrdersCrud from 'containers/SalesOrders/hooks/useSalesOrdersCrud';
import useCopilotBarCrudHeaderToggle from 'hooks/useCopilotBarCrudHeaderToggle';
import { publish } from 'lib/EventBuser';
import { ENTITY_MODAL_UPDATE } from 'lib/events';
import SageCopilotIcon from 'lib/metadata/icons/SageCopilot';
import { ACTIVITY_TYPE_CALL, ACTIVITY_TYPE_EMAIL } from 'models/ActivityModel';
import { getActiveCrud } from 'utils/crud';
import { hasExternalCrud } from 'utils/externalCrud';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { getLiteral } from 'utils/getLiteral';
import { ensureRoute } from 'utils/routes';
import { errorToast, successToast } from 'utils/toast';
import { logEvent } from 'utils/tracking';

import EntityCrudContent from '../EntityCrud/EntityCrudContent';
import EntityCrudGeneric from '../EntityCrud/EntityCrudGeneric';
import EntityCrudWrapper from '../EntityCrud/EntityCrudWrapper';
import {
    ABSOLUTE_FOOTER_VERTICAL_PADDING,
    useScrollInsetForAbsoluteFooter,
} from '../EntityDetail/hooks/useScrollInsetForAbsoluteFooter';
import EntityModalPlaceholder from './components/EntityModalPlaceholder';
import './styles.scss';

const ENTITIES_WITH_DETAIL = [COMPANIES, CONTACTS, ACTIVITIES, OPPORTUNITIES, SALESORDERS, KPIS];

const EntityModal = () => {
    const dispatch = useDispatch();
    const salesOrdersCrud = useSalesOrdersCrud();

    // ========================================
    // CONTEXT & HOOKS
    // ========================================
    const { copilotBarProps } = useContext(CopilotContext);
    const {
        height: copilotBarHeight,
        isMinimized: isCopilotBarMinimized,
        isOpen: isCopilotBarOpen,
        padding: copilotBarPadding,
    } = copilotBarProps;

    const {
        isToggleDisabled: isCopilotBarToggleDisabled,
        onCopilotBarToggle,
        preserveCustomIconColors: copilotBarIconPreserveColors,
        showToggle: showCopilotBarToggle,
    } = useCopilotBarCrudHeaderToggle();

    // ========================================
    // REDUX STATE & SELECTORS
    // ========================================
    const entityCrud = useSelector((state) => getActiveCrud(state));
    const activeModal = useSelector((state) => state.entityModal[state.entityModal.active] || {});
    const isOpen = useSelector((state) => state.entityModal.isOpen);
    const isConfirmModalOpen = useSelector((state) => state.entityCrud?.isConfirmModalOpen);
    const entityList = useSelector((state) => state.entityList);
    const crudPermissions = useSelector((state) => state.config.permission?.crud_permission || {});
    const userData = useSelector((state) => state.config?.userData || {});
    const duplicateControlMode = useSelector((state) =>
        String(state.config.permission?.checkDuplicateFields ?? ''),
    );

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================
    const entity = activeModal?.entity;
    const labels = activeModal?.labels;
    const extraInfo = activeModal?.extraInfo;
    const forceRefresh = activeModal?.forceRefresh;
    const refreshIf = activeModal?.refreshIf || null;
    const isCreateGlobal = activeModal?.isCreateGlobal || false;
    const isDetailCreateGlobal = activeModal?.isDetailCreateGlobal || false;
    const hasMiddleButton = activeModal?.hasMiddleButton;
    const size = activeModal?.size || 'large';
    const hideDelete = activeModal?.hideDelete;
    const useS200ukiSalesOrdersEmbed =
        entity?.entity === SALESORDERS.entity && hasExternalCrud(SALESORDERS.entity, userData);
    const loading = entityCrud?.loading ?? true;
    const loadingSave = entityCrud?.loadingSave || false;
    const data = entityCrud?.data;
    const originalData = entityCrud?.originalData;
    const trackingSubmodule = entityCrud?.trackingSubmodule;
    const bottomPanel = useMemo(() => {
        if (entity?.entity === SALESORDERS.entity) return salesOrdersCrud.bottomPanel;

        return entityCrud?.bottomPanel;
    }, [entity?.entity, entityCrud?.bottomPanel, salesOrdersCrud.bottomPanel]);

    const canDelete = useMemo(() => {
        return (
            entityCrud?.id &&
            !getBackendBoolean(entityCrud?.data?.isReadOnly) &&
            crudPermissions?.[entity?.permission]?.delete
        );
    }, [entityCrud?.id, entityCrud?.data?.isReadOnly, crudPermissions, entity?.permission]);

    const canSave = useMemo(() => {
        if (entityCrud?.data?.isReadOnly || !entityCrud?.data || activeModal.disableConfirm) {
            return false;
        }
        return true;
    }, [entityCrud?.data, activeModal.disableConfirm]);

    const preComponents = useMemo(() => entityCrud.preComponents || [], [entityCrud.preComponents]);
    const modalContentOverride = useMemo(
        () => ({
            container: isCopilotBarOpen
                ? {
                    style: {
                        height: `calc(100vh - ${copilotBarHeight}px - ${copilotBarPadding * 5}px)`,
                        transform: !isCopilotBarMinimized
                            ? `translateY(-${(copilotBarHeight - copilotBarPadding * 5) / 2 + copilotBarPadding * 2}px)`
                            : 'translateY(0)',
                    },
                }
                : null,
            content: {
                className: 'fm-entity-modal__content',
            },
            confirmButton: {
                isLoading: loadingSave && !isMiddleButtonClicked,
                isDisabled: !canSave || loading || (isMiddleButtonClicked && loadingSave),
            },
            header: isCopilotBarOpen ? { style: { alignItems: 'center' } } : null,
            middleButton: {
                isLoading: isMiddleButtonClicked && loadingSave,
                isDisabled: !canSave || loading || (!isMiddleButtonClicked && loadingSave),
                className: 'fm-entity-modal__middle-button',
            },
        }),
        [
            canSave,
            copilotBarHeight,
            copilotBarPadding,
            isCopilotBarMinimized,
            isCopilotBarOpen,
            isMiddleButtonClicked,
            loading,
            loadingSave,
        ],
    );

    const modalDeleteContentOverride = useMemo(
        () => ({
            confirmButton: { isLoading: isLoadingDelete, type: 'primary-error' },
        }),
        [isLoadingDelete],
    );

    const renderPreComponents = useMemo(() => {
        if (!preComponents?.length) return null;
        return preComponents.reduce((arr, current) => {
            if (current.component === 'advice' && current.content) {
                const componentProps = current.componentProps || {};
                const content = current.content || null;
                arr.push(
                    <Advice className="fm-entity-modal__advice-preComponent" {...componentProps}>
                        {content}
                    </Advice>,
                );
            }

            return arr;
        }, []);
    }, [preComponents]);

    const bottomPanelContent = useMemo(() => {
        if (!bottomPanel?.render) return null;

        return bottomPanel.render({ data, entity });
    }, [bottomPanel, data, entity]);

    const hasBottomFooter = !!bottomPanelContent && !loading;
    const declaredBottomInset = bottomPanelContent
        ? (bottomPanel?.height || 0) + ABSOLUTE_FOOTER_VERTICAL_PADDING
        : 0;
    const { footerRef, inset: bottomPanelScrollInset } = useScrollInsetForAbsoluteFooter(
        hasBottomFooter,
        declaredBottomInset,
    );

    const renderTitle = useMemo(
        () => (
            <>
                <div className="fm-entity-modal__title">
                    <span className="fm-entity-modal__title__text">{labels?.title}</span>
                    <UsageLimitInfo entity={entity} />
                </div>
                {showCopilotBarToggle && (
                    <Button
                        className="fm-entity-modal__copilot-bar-toggle"
                        customIcon={<SageCopilotIcon />}
                        isDisabled={isCopilotBarToggleDisabled}
                        onClick={onCopilotBarToggle}
                        preserveCustomIconColors={copilotBarIconPreserveColors}
                        type="promotion"
                    >
                        {getLiteral('label_copilot')}
                    </Button>
                )}
            </>
        ),
        [
            copilotBarIconPreserveColors,
            entity,
            isCopilotBarToggleDisabled,
            labels?.title,
            onCopilotBarToggle,
            showCopilotBarToggle,
        ],
    );

    // ========================================
    // LOCAL STATE
    // ========================================
    const [isLoadingDelete, setLoadingDelete] = useState(false);
    const [isOpenDelete, setIsOpenDelete] = useState(false);
    const [isMiddleButtonClicked, setIsMiddleButtonClicked] = useState(false);
    const isMiddleButtonRef = useRef(false);

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const cancel = useCallback(() => {
        dispatch(EntityModalActions.cancel());
    }, [dispatch]);

    const save = useCallback(
        (params) => {
            return dispatch(EntityCrudActions.save(params));
        },
        [dispatch],
    );

    const deleteEntity = useCallback(
        (entityParam) => {
            return dispatch(EntityCrudActions.deleteEntity(entityParam));
        },
        [dispatch],
    );

    const toggleConfirmModal = useCallback(
        (isOpen) => {
            dispatch(EntityCrudActions.toggleConfirmModal(isOpen));
        },
        [dispatch],
    );

    const onDelete = useCallback(() => {
        setIsOpenDelete(true);
    }, []);

    const onConfirmDelete = useCallback(() => {
        setLoadingDelete(true);
        deleteEntity(entity)
            .then(() => {
                successToast({ text: labels.successDelete || labels.success });
                publish(`${ENTITY_MODAL_UPDATE}--${entity.entity}`);
                cancel();
                logEvent({
                    event: entity.trueName,
                    functionality: 'delete',
                });
            })
            .catch((error) => {
                console.error(error);
                errorToast({ text: labels.error });
            })
            .then(() => {
                setIsOpenDelete(false);
                setLoadingDelete(false);
            });
    }, [cancel, deleteEntity, entity, labels]);

    const onCancelDelete = useCallback(() => {
        setIsOpenDelete(false);
    }, []);

    const getDetailRedirection = useCallback(
        (result) => {
            const id = result?.Insert?.id || result.id || null;
            let routeText = `${getLiteral('label_see')} ${getLiteral(
                entity.labels.plural,
            ).toLowerCase()}`;
            let route = `${entity.route}`;
            if (entity?.route && id && ENTITIES_WITH_DETAIL.includes(entity)) {
                route = `${route}/${id}`;
                routeText = `${getLiteral('label_view_detail')}`;
                if (extraInfo?.activityType) route = `${route}/${extraInfo?.activityType}`;
            }

            const calendarPage = [AGENDA.entity, TASKS.entity];

            // if we are creating an entity from the same entity and it does not has detail
            // we don't send any route
            if (entity.entity === entityList.active && !ENTITIES_WITH_DETAIL.includes(entity)) {
                return {};
            } else if (
                entity.entity !== entityList.active &&
                calendarPage.includes(entityList.active)
            ) {
                return {};
            }

            return { route, routeText };
        },
        [entity, extraInfo, entityList],
    );

    const getCreationName = useCallback(() => {
        const fieldsByEntity = {
            [COMPANIES.entity]: ['name'],
            [CONTACTS.entity]: ['Name', 'Surname'],
            [AGENDA.entity]: ['asunto'],
            [TASKS.entity]: ['asunto'],
            [OPPORTUNITIES.entity]: ['reference'],
        };

        const fields = fieldsByEntity[entity.entity] || [];
        let text = fields.reduce((str, current) => {
            if (!data[current]) return str;
            if (!str) str = data[current];
            else str = `${str} ${data[current]}`;
            return str;
        }, '');

        if (text) {
            text = `${getLiteral(entity.labels.singular)} ${text} ${getLiteral(
                'label_created_new',
            )}`;
        } else {
            text = `${getLiteral(entity.labels.singular)} ${getLiteral('label_created_new')}`;
        }
        return text;
    }, [data, entity]);

    const saveMethod = useCallback(
        (refreshIf) => {
            let pr = Promise.resolve();

            let info = {};
            if (refreshIf) info.refreshIf = refreshIf;

            if (!loadingSave)
                pr = save({
                    entity,
                    openDetail: false,
                    hasCrudInDetail: false,
                    fieldsToUpdate: null,
                    fromWorkFlow: false,
                    forceRefresh: forceRefresh || false,
                    info,
                });
            return pr
                .then((result) => {
                    let trackingPayload = null;
                    switch (true) {
                        case !data.id:
                            trackingPayload = {
                                event: entity.trueName,
                                functionality: 'create',
                            };
                            if (isCreateGlobal) trackingPayload.submodule = 'createGlobal';
                            if (isDetailCreateGlobal) {
                                trackingPayload.submodule = 'detailCreateGlobal';
                            }
                            if (trackingSubmodule) trackingPayload.submodule = trackingSubmodule;
                            break;
                        case data.id &&
                            entity.trueName === ACTIVITIES.trueName &&
                            data.activityType === ACTIVITY_TYPE_CALL.toString():
                            trackingPayload = {
                                event: entity.trueName,
                                submodule: 'activitiesWidget',
                                functionality: 'phoneCallUpdate',
                            };
                            break;
                        case data.id &&
                            entity.trueName === ACTIVITIES.trueName &&
                            data.activityType === ACTIVITY_TYPE_EMAIL.toString():
                            trackingPayload = {
                                event: entity.trueName,
                                submodule: 'activitiesWidget',
                                functionality: 'emailUpdate',
                            };
                            break;
                        default:
                            break;
                    }
                    if (trackingPayload) logEvent(trackingPayload);
                    if (
                        result?.Status?.code === 'DuplicatedEntity' &&
                        duplicateControlMode === '2'
                    ) {
                        errorToast({ text: getLiteral('title_duplicated_account_message') });
                    } else {
                        let text = '';
                        let route = '';
                        let routeText = '';
                        if (isCreateGlobal) {
                            text = getCreationName();
                            if (!isMiddleButtonRef.current) {
                                const detailRedirection = getDetailRedirection(result);
                                route = detailRedirection?.route || '';
                                routeText = detailRedirection?.routeText || '';
                            }
                            isMiddleButtonRef.current = false;
                        }

                        successToast({
                            text: text || labels.success,
                            onClickLink: () => {
                                logEvent({
                                    event: entity.trueName,
                                    submodule: 'successToast',
                                    functionality: 'buttonToastDetailAndList',
                                });
                                ensureRoute(route);
                            },
                            linkText: routeText,
                        });
                        publish(`${ENTITY_MODAL_UPDATE}--${entity.entity}`, { result, data });
                        cancel();
                    }
                    return result;
                })
                .catch((errors) => {
                    console.error(errors);
                    errors.serverError && labels.error && errorToast({ text: labels.error });
                });
        },
        [
            cancel,
            data,
            entity,
            labels,
            loadingSave,
            save,
            forceRefresh,
            isCreateGlobal,
            isDetailCreateGlobal,
            getCreationName,
            getDetailRedirection,
            trackingSubmodule,
            duplicateControlMode,
        ],
    );

    const onConfirm = useCallback(() => {
        saveMethod(refreshIf);
    }, [saveMethod, refreshIf]);

    const onMiddleButton = useCallback(() => {
        if (!hasMiddleButton) return;
        setIsMiddleButtonClicked(true);
        isMiddleButtonRef.current = true;
        saveMethod()
            .then((result) => {
                if (
                    !result ||
                    (result?.Status?.code === 'DuplicatedEntity' && duplicateControlMode === '2')
                ) {
                    setIsMiddleButtonClicked(false);
                    return;
                }

                const trackingPayload = {
                    event: entity.trueName,
                    functionality: 'navigateAfterCreate',
                };

                if (isCreateGlobal) trackingPayload.submodule = 'createGlobal';

                logEvent(trackingPayload);

                const id = result?.Insert?.id || result?.id || result?.Insert?.[0]?.id || null;
                let route = `${entity.route}`;
                if (entity?.route && id && ENTITIES_WITH_DETAIL.includes(entity)) {
                    route = `${route}/${id}`;
                    if (extraInfo?.activityType) route = `${route}/${extraInfo?.activityType}`;
                }

                ensureRoute(route);
                setIsMiddleButtonClicked(false);
            })
            .catch((err) => {
                console.error(err);
                setIsMiddleButtonClicked(false);
            });
    }, [entity, extraInfo, hasMiddleButton, saveMethod, isCreateGlobal, duplicateControlMode]);

    const onConfirmCancelModal = useCallback(() => {
        toggleConfirmModal(false);
        cancel();
    }, [cancel, toggleConfirmModal]);

    const checkChanges = useCallback(() => {
        isEqual(originalData, data, true) || !originalData || !data
            ? onConfirmCancelModal()
            : toggleConfirmModal(true);
    }, [data, onConfirmCancelModal, originalData, toggleConfirmModal]);

    const onCloseCancelModal = useCallback(() => {
        toggleConfirmModal(false);
    }, [toggleConfirmModal]);

    // ========================================
    // EFFECTS
    // ========================================
    useEffect(() => {
        return () => {
            if (isMiddleButtonClicked) setIsMiddleButtonClicked(false);
        };
    }, [isMiddleButtonClicked]);

    // ========================================
    // MEMOIZED PROPS
    // ========================================
    const mainModalProps = useMemo(
        () => ({
            cancelText: getLiteral('action_cancel'),
            className: 'fm-entity-modal__container',
            confirmText: getLiteral('action_save'),
            deleteText: getLiteral('action_delete'),
            isOpen,
            middleButtonText: labels?.middleButtonText || '',
            onCancel: checkChanges,
            onConfirm,
            onDelete: canDelete && !hideDelete ? onDelete : undefined,
            onMiddleButton: hasMiddleButton && onMiddleButton,
            onRequestClose: checkChanges,
            overlayClassName: 'fm-entity-modal__overlay',
            overrides: modalContentOverride,
            shouldCloseOnOverlayClick: false,
            size,
            title: renderTitle,
            useContentStaticHeight: true,
            useCornerClose: true,
        }),
        [
            canDelete,
            checkChanges,
            hasMiddleButton,
            hideDelete,
            isOpen,
            labels?.middleButtonText,
            modalContentOverride,
            onConfirm,
            onDelete,
            onMiddleButton,
            renderTitle,
            size,
        ],
    );

    const deleteModalProps = useMemo(
        () => ({
            cancelText: getLiteral('action_cancel'),
            className: 'fm-entity-modal__container',
            confirmText: getLiteral('helptext_confirm'),
            isOpen: isOpenDelete,
            onCancel: onCancelDelete,
            onConfirm: onConfirmDelete,
            onRequestClose: onCancelDelete,
            overlayClassName: 'fm-entity-modal__delete-overlay',
            overrides: modalDeleteContentOverride,
            size: 'small',
            title: labels?.deleteModalTitle,
        }),
        [
            isOpenDelete,
            labels?.deleteModalTitle,
            modalDeleteContentOverride,
            onCancelDelete,
            onConfirmDelete,
        ],
    );

    const confirmCancelModalProps = useMemo(
        () => ({
            cancelText: getLiteral('action_cancel'),
            confirmText: getLiteral('action_discard_changes'),
            isOpen: isConfirmModalOpen,
            onCancel: onCloseCancelModal,
            onConfirm: onConfirmCancelModal,
            overlayClassName: 'fm-entity-modal__overlay-cancel',
            size: 'small',
            title: getLiteral('label_discard_unsaved_changes'),
            useCornerClose: false,
        }),
        [isConfirmModalOpen, onCloseCancelModal, onConfirmCancelModal],
    );

    // ========================================
    // RENDER
    // ========================================
    if (useS200ukiSalesOrdersEmbed) {
        return (
            <SalesOrdersEmbeddedCrud
                isOpen={isOpen}
                mode={entityCrud?.id ? 'edit' : 'create'}
                onClose={cancel}
                title={labels?.title}
            />
        );
    }

    return (
        <Modal {...mainModalProps}>
            {renderPreComponents}
            {!loading && (
                <EntityCrudWrapper>
                    <EntityCrudContent
                        bottomSpacing={bottomPanelScrollInset}
                        includeCopilotInBottomInset={false}
                    >
                        <EntityCrudGeneric />
                    </EntityCrudContent>
                    {!!bottomPanelContent && (
                        <div
                            ref={footerRef}
                            className={`entity-crud-bottom-panel ${bottomPanel.className || ''}`}
                        >
                            {bottomPanelContent}
                        </div>
                    )}
                </EntityCrudWrapper>
            )}
            {loading && isOpen && <EntityModalPlaceholder size="medium" />}
            {canDelete && (
                <Modal {...deleteModalProps}>
                    <Text>{getLiteral('confirm_delete')}</Text>
                </Modal>
            )}
            <Modal {...confirmCancelModalProps}></Modal>
        </Modal>
    );
};

export default memo(EntityModal);
